/**
 * O cartão de uma imagem candidata (E87.F2 — GUS-31 RF02/CA03).
 *
 * Mostra a imagem, de qual documento e página ela saiu e o tamanho dela, e
 * oferece os dois destinos possíveis — e, desde a E109.F1 (S-08), a terceira
 * saída: "não é retrato", que tira a imagem das propostas. Desde a E111.F1 (N-14), "não é
 * retrato" é só isso; tirar a imagem da análise é ação própria, com motivo e registro, e só vale
 * para a imagem que é o arquivo inteiro do material (a página de um PDF segue no documento). A procedência fica ao lado da imagem, e
 * não escondida num rótulo: aceitar uma candidata é afirmar que aquele rosto
 * veio daquele documento, e o perito não pode ter de confiar na tela para isso.
 *
 * E122.F2 (ADR-0079 E.23): a classe lida da imagem aparece ao lado do tamanho e destaca o
 * destino sugerido; a candidata que já entrou sem clique diz isso e trava os destinos — trocar
 * é pela galeria de fotos.
 *
 * E162.F1.T08 (ADR-0111 §2.4, QA-25): a candidata cujo documento não tem vínculo demonstrado com
 * o contrato e a pessoa do caso sai marcada — o perito pode confirmá-la, e ela não corrobora.
 *
 * A rota dos bytes exige autenticação no cabeçalho, então a imagem não vai
 * direto no `src` — mesmo padrão da miniatura das fotos do signatário.
 */
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  baixarCandidataDeImagem,
  motivoSemVinculo,
  type CandidataDeImagemData,
  type LeituraDaImagemData,
} from '@/lib/candidatas_de_imagem';
import type { DocumentoDeApoioData } from '@/lib/documentos_de_apoio';
import type { GrupoDaFoto } from '@/lib/fotos_do_signatario';

import { ExclusaoDaAnalise } from './ExclusaoDaAnalise';
import { LeituraPropostaDaImagem } from './LeituraPropostaDaImagem';

const t = ptBR.analiseFotografica.candidatas;

export interface CandidataDeImagemCardProps {
  caseId: string;
  candidata: CandidataDeImagemData;
  confirmando: GrupoDaFoto | null;
  bloqueado: boolean;
  onConfirmar: (candidata: CandidataDeImagemData, grupo: GrupoDaFoto) => void;
  /** E109.F1: "não é retrato / excluir". Sem ele, o cartão não oferece a saída. */
  onExcluir?: (candidata: CandidataDeImagemData) => void;
  excluindo?: boolean;
  /** E191.F1 (GT-2026-0030): "Ler os dados deste documento" na imagem de documento de identidade. */
  onLer?: (candidata: CandidataDeImagemData) => void;
  lendo?: boolean;
  /** E111.F1: a imagem inteira saiu da análise. Sem ele, o cartão não oferece a exclusão. */
  onExcluidaDaAnalise?: (documento: DocumentoDeApoioData) => void;
  /** E114.F3 (R-05): a leitura proposta da imagem de documento, confirmada aqui. */
  leitura?: LeituraDaImagemData | undefined;
  onLeituraConfirmada?: () => void;
  /** E122.F2 (ADR-0079 E.23): esta candidata já é foto do caso, confirmada sem clique. */
  confirmadaAutomaticamente?: boolean;
  /**
   * E123.F3 ("Trocar fotos"): esta candidata é a fotografia escolhida agora para o grupo — o cartão
   * diz isso e trava só esse destino; os outros continuam, para a troca.
   */
  emUso?: GrupoDaFoto | null;
}

/** E122.F2: o grupo que a classe lida sugere; `null` sem classe ou para verso e outras. */
function grupoSugerido(candidata: CandidataDeImagemData): GrupoDaFoto | null {
  if (candidata.classe === 'identidade_frente') return 'documento_identidade';
  if (candidata.classe === 'retrato') return 'ccb';
  return null;
}

function Miniatura({
  caseId,
  candidata,
  alt,
}: {
  caseId: string;
  candidata: CandidataDeImagemData;
  alt: string;
}): JSX.Element {
  const client = useApiClient();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    let criada: string | null = null;
    void (async () => {
      try {
        const blob = await baixarCandidataDeImagem(client, caseId, candidata);
        if (cancelado) return;
        criada = URL.createObjectURL(blob);
        setUrl(criada);
      } catch {
        if (!cancelado) setUrl(null);
      }
    })();
    return () => {
      cancelado = true;
      if (criada !== null) URL.revokeObjectURL(criada);
    };
  }, [client, caseId, candidata]);

  return (
    <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden border border-border bg-surface-2">
      {url === null ? (
        <span className="text-caption text-fg-dim">{t.carregando}</span>
      ) : (
        <img src={url} alt={alt} className="h-full w-full object-cover" />
      )}
    </div>
  );
}

/**
 * E191.F1 (GT-2026-0030): "Ler os dados deste documento" — só na imagem lida como face de
 * documento de identidade, e só onde a seção oferece a leitura (`onLer`).
 */
function LerDocumento({
  candidata,
  travado,
  lendo,
  onLer,
}: {
  candidata: CandidataDeImagemData;
  travado: boolean;
  lendo: boolean;
  onLer: ((candidata: CandidataDeImagemData) => void) | undefined;
}): JSX.Element | null {
  const deDocumento =
    candidata.classe === 'identidade_frente' || candidata.classe === 'identidade_verso';
  if (onLer === undefined || !deDocumento) return null;
  return (
    <Button
      type="button"
      variant="secondary"
      disabled={travado}
      onClick={() => onLer(candidata)}
      data-testid={`candidata-${candidata.codigo}-ler`}
    >
      {lendo ? t.lendoDocumento : t.lerDocumento}
    </Button>
  );
}

function Destinos({
  candidata,
  confirmando,
  travado,
  emUso,
  onConfirmar,
  onExcluir,
  excluindo,
  onLer,
  lendo,
}: {
  candidata: CandidataDeImagemData;
  confirmando: GrupoDaFoto | null;
  travado: boolean;
  emUso: GrupoDaFoto | null;
  onConfirmar: (candidata: CandidataDeImagemData, grupo: GrupoDaFoto) => void;
  onExcluir: ((candidata: CandidataDeImagemData) => void) | undefined;
  excluindo: boolean;
  onLer: ((candidata: CandidataDeImagemData) => void) | undefined;
  lendo: boolean;
}): JSX.Element {
  const sugerido = grupoSugerido(candidata);
  return (
    <div className="flex flex-wrap items-center gap-sc-2">
      <span className="text-caption text-fg-muted">{t.usarComo}</span>
      <Button
        type="button"
        variant={sugerido === 'documento_identidade' ? 'primary' : 'secondary'}
        disabled={travado || emUso === 'documento_identidade'}
        onClick={() => onConfirmar(candidata, 'documento_identidade')}
        data-testid={`candidata-${candidata.codigo}-identidade`}
      >
        {confirmando === 'documento_identidade' ? t.confirmando : t.usarComoIdentidade}
      </Button>
      <Button
        type="button"
        variant={sugerido === 'ccb' ? 'primary' : 'secondary'}
        disabled={travado || emUso === 'ccb'}
        onClick={() => onConfirmar(candidata, 'ccb')}
        data-testid={`candidata-${candidata.codigo}-ccb`}
      >
        {confirmando === 'ccb' ? t.confirmando : t.usarComoCcb}
      </Button>
      <LerDocumento candidata={candidata} travado={travado} lendo={lendo} onLer={onLer} />
      {onExcluir !== undefined ? (
        <Button
          type="button"
          variant="ghost"
          disabled={travado}
          onClick={() => onExcluir(candidata)}
          data-testid={`candidata-${candidata.codigo}-excluir`}
        >
          {excluindo ? t.excluindo : t.excluir}
        </Button>
      ) : null}
    </div>
  );
}

/** E122.F2 e E123.F3: a candidata em uso num grupo, ou a que entrou sem clique. */
function MarcaDaEscolha({
  codigo,
  emUso,
  confirmadaAutomaticamente,
}: {
  codigo: string;
  emUso: GrupoDaFoto | null;
  confirmadaAutomaticamente: boolean;
}): JSX.Element | null {
  if (emUso !== null) {
    return (
      <p
        role="status"
        className="text-body-sm text-success-700"
        data-testid={`candidata-${codigo}-em-uso`}
      >
        {t.emUso(ptBR.exameEletronico.fotografias.grupos[emUso])}
      </p>
    );
  }
  if (!confirmadaAutomaticamente) return null;
  return (
    <p
      role="status"
      className="text-body-sm text-success-700"
      data-testid={`candidata-${codigo}-automatica`}
    >
      {t.confirmadaAutomaticamente}
    </p>
  );
}

/** E162.F1.T08: o documento de origem não tem vínculo demonstrado com o caso. */
function SeloDoVinculo({ candidata }: { candidata: CandidataDeImagemData }): JSX.Element | null {
  const motivo = motivoSemVinculo(candidata.vinculo);
  if (motivo === null) return null;
  return (
    <div
      role="status"
      className="border border-warning bg-warning-50 px-sc-3 py-sc-2 text-warning-700"
      data-testid={`candidata-${candidata.codigo}-sem-vinculo`}
      data-motivo={motivo}
    >
      <p className="text-body-sm font-semibold">
        {t.vinculo.semVinculo}: {t.vinculo.motivo[motivo]}.
      </p>
      <p className="mt-1 max-w-prose text-caption">{t.vinculo.efeito}</p>
    </div>
  );
}

export function CandidataDeImagemCard({
  caseId,
  candidata,
  confirmando,
  bloqueado,
  onConfirmar,
  onExcluir,
  excluindo = false,
  onLer,
  lendo = false,
  onExcluidaDaAnalise,
  leitura,
  onLeituraConfirmada,
  confirmadaAutomaticamente = false,
  emUso = null,
}: CandidataDeImagemCardProps): JSX.Element {
  const procedencia =
    candidata.pagina === null
      ? candidata.documento_nome
      : t.provenienciaComPagina(candidata.documento_nome, candidata.pagina);
  const classe = candidata.classe ?? null;

  return (
    <li
      className="flex gap-sc-4 border border-border p-sc-3"
      data-testid={`candidata-${candidata.codigo}`}
    >
      <Miniatura caseId={caseId} candidata={candidata} alt={procedencia} />

      <div className="min-w-0 flex-1 space-y-sc-2">
        <p className="truncate text-body-sm text-fg" title={procedencia}>
          <span className="text-fg-muted">{t.proveniencia}: </span>
          <span data-testid={`candidata-${candidata.codigo}-procedencia`}>{procedencia}</span>
        </p>
        <p className="text-caption text-fg-dim">
          {t.tamanho(candidata.largura, candidata.altura)}
          {classe !== null ? (
            <span className="ml-sc-2" data-testid={`candidata-${candidata.codigo}-classe`}>
              · {t.classe[classe]}
            </span>
          ) : null}
        </p>
        <MarcaDaEscolha
          codigo={candidata.codigo}
          emUso={emUso}
          confirmadaAutomaticamente={confirmadaAutomaticamente}
        />
        <SeloDoVinculo candidata={candidata} />
        <Destinos
          candidata={candidata}
          confirmando={confirmando}
          // E123.F3: na troca, a escolha do sistema não trava a candidata — trocar é o ponto.
          travado={bloqueado || (confirmadaAutomaticamente && emUso === null)}
          emUso={emUso}
          onConfirmar={onConfirmar}
          onExcluir={onExcluir}
          excluindo={excluindo}
          onLer={onLer}
          lendo={lendo}
        />
        {leitura !== undefined ? (
          <LeituraPropostaDaImagem
            caseId={caseId}
            leitura={leitura}
            bloqueado={bloqueado}
            onConfirmada={() => onLeituraConfirmada?.()}
          />
        ) : null}
        {onExcluidaDaAnalise !== undefined && candidata.pagina === null ? (
          <ExclusaoDaAnalise
            caseId={caseId}
            documentoId={candidata.documento_id}
            exclusao={null}
            bloqueado={bloqueado}
            onAlterado={onExcluidaDaAnalise}
          />
        ) : null}
      </div>
    </li>
  );
}
